export const EN_US_LOCALE = 'en-US';
export const NL_NL_LOCALE = 'nl-NL';
export const DE_DE_LOCALE = 'de-DE';
export const FR_FR_LOCALE = 'fr-FR';
export const ES_ES_LOCALE = 'es-ES';
export const IT_IT_LOCALE = 'it-IT';
export const DA_DK_LOCALE = 'da-DK';
export const NB_NO_LOCALE = 'nb-NO';
export const SV_SE_LOCALE = 'sv-SE';
export const LOCALE_OVERRIDE_STORAGE_KEY = 'spothr.localeOverride';

export type SupportedLocale =
  | typeof EN_US_LOCALE
  | typeof NL_NL_LOCALE
  | typeof DE_DE_LOCALE
  | typeof FR_FR_LOCALE
  | typeof ES_ES_LOCALE
  | typeof IT_IT_LOCALE
  | typeof DA_DK_LOCALE
  | typeof NB_NO_LOCALE
  | typeof SV_SE_LOCALE;

export interface SupportedLocaleOption {
  aliases: string[];
  label: string;
  locale: SupportedLocale;
  shortLabel: string;
}

export const supportedLocaleOptions: SupportedLocaleOption[] = [
  {
    aliases: ['en'],
    label: 'English',
    locale: EN_US_LOCALE,
    shortLabel: 'EN'
  },
  {
    aliases: ['nl'],
    label: 'Nederlands',
    locale: NL_NL_LOCALE,
    shortLabel: 'NL'
  },
  {
    aliases: ['de'],
    label: 'Deutsch',
    locale: DE_DE_LOCALE,
    shortLabel: 'DE'
  },
  {
    aliases: ['fr'],
    label: 'Français',
    locale: FR_FR_LOCALE,
    shortLabel: 'FR'
  },
  {
    aliases: ['es'],
    label: 'Español',
    locale: ES_ES_LOCALE,
    shortLabel: 'ES'
  },
  {
    aliases: ['it'],
    label: 'Italiano',
    locale: IT_IT_LOCALE,
    shortLabel: 'IT'
  },
  {
    aliases: ['da'],
    label: 'Dansk',
    locale: DA_DK_LOCALE,
    shortLabel: 'DA'
  },
  {
    aliases: ['no', 'nb', 'nn', 'nn-no'],
    label: 'Norsk',
    locale: NB_NO_LOCALE,
    shortLabel: 'NO'
  },
  {
    aliases: ['sv'],
    label: 'Svenska',
    locale: SV_SE_LOCALE,
    shortLabel: 'SV'
  }
];

const normalizedLocaleLookup: Record<string, SupportedLocale> = {};

supportedLocaleOptions.forEach(option => {
  normalizedLocaleLookup[option.locale.toLowerCase()] = option.locale;
  option.aliases.forEach(alias => {
    normalizedLocaleLookup[alias] = option.locale;
  });
});

export const getSupportedLocaleOption = (
  locale: SupportedLocale
): SupportedLocaleOption | undefined =>
  supportedLocaleOptions.find(option => option.locale === locale);

export const normalizeLocale = (
  locale?: string | null
): SupportedLocale | undefined => {
  if (!locale) {
    return undefined;
  }

  return normalizedLocaleLookup[locale.toLowerCase()];
};

export const getLocaleOverride = (): SupportedLocale | undefined =>
  normalizeLocale(localStorage.getItem(LOCALE_OVERRIDE_STORAGE_KEY));

export const setLocaleOverride = (locale: SupportedLocale) => {
  localStorage.setItem(LOCALE_OVERRIDE_STORAGE_KEY, locale);
};

export const clearLocaleOverride = () => {
  localStorage.removeItem(LOCALE_OVERRIDE_STORAGE_KEY);
};

export const getBrowserLocale = (): SupportedLocale => {
  const browserLocales = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];

  for (let i = 0; i < browserLocales.length; i += 1) {
    const normalized = normalizeLocale(browserLocales[i]);
    if (normalized) {
      return normalized;
    }
  }

  return EN_US_LOCALE;
};

export const resolveInitialLocale = (): SupportedLocale =>
  getLocaleOverride() || getBrowserLocale();
